import React, { useState, useRef, useEffect } from 'react';
import { PrimaryButtonSmall } from './Button';
import { callFunction } from '../api/callFunction';
import { showToast } from '../utils/showToast';
import { fetchFunction } from '../api/fetchFunction';
import { parseMarkdown } from '../utils/parseMarkdown';
import { readSSEStream } from '../utils/readSSEStream';

export const getInitialMessage = (type, eventData) => {
  if (type === 'email') {
    return `Would you like me to find the contact **email** for the **${eventData?.hostname},** ${eventData?.address}?`;
  }
  if (type === 'superAdmin') {
    return 'Hello! I have access to the events collection and can update a few fields. Ask me anything about your events.';
  }
  return 'What kind of event would you like to create?';
};

const TOOL_LABELS = {
  query_collection: 'Querying collection...',
  get_document: 'Fetching document...',
  aggregate_collection: 'Counting documents...',
  update_event: 'Updating event...',
  web_search: 'Searching the web...',
  run_code: 'Running code...',
  list_subcollection: 'Exploring subcollection...',
};

const AITextExtractor = ({
  onExtractedData,
  type = 'event',
  eventData,
  onAccept,
  initialCollapsed = false,
  initialMessage = '',
  fullHeight = false,
  // Controlled mode: when `messages` is provided the parent owns the
  // conversation history (e.g. AdminAgent persists chats to Firestore).
  // `onMessagesChange` must behave like a setState setter (accepts a value
  // or an updater function).
  messages,
  onMessagesChange,
  onNewChat,
  onProcessingChange,
}) => {
  const isControlled = messages !== undefined;
  const [internalHistory, setInternalHistory] = useState([
    { role: 'assistant', content: getInitialMessage(type, eventData) }
  ]);
  const conversationHistory = isControlled ? messages : internalHistory;
  const setConversationHistory = isControlled ? onMessagesChange : setInternalHistory;
  const [currentMessage, setCurrentMessage] = useState(initialMessage);
  const [isProcessing, setIsProcessingState] = useState(false);

  const setIsProcessing = (value) => {
    setIsProcessingState(value);
    onProcessingChange?.(value);
  };
  const [extractedData, setExtractedData] = useState(null);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');
  const [isChatCollapsed, setIsChatCollapsed] = useState(initialCollapsed);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setIsChatCollapsed(initialCollapsed);
  }, [initialCollapsed]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [conversationHistory, isProcessing, pendingMessage]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    if (!currentMessage) {
      el.style.height = '';
      return;
    }
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [currentMessage]);

  const handleSendMessage = async () => {
    if (!currentMessage.trim() || isProcessing) return;

    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    setIsProcessing(true);
    inputRef.current?.focus();
    setPendingMessage({ role: 'user', content: userMessage });

    try {
      if (type === 'email') {
        const response = await callFunction('conversationEmailGeneratorCallable', { conversationHistory, userMessage, eventData }, { timeout: 540000 });

        if (response.type === 'email') {
          setExtractedData(response.data);
          onExtractedData(response.data);
          setConversationHistory(response.conversationHistory);
          setIsChatCollapsed(true);
          showToast('Email generated successfully!', { type: 'success' });
        } else {
          setConversationHistory(response.conversationHistory);
        }
      } else if (type === 'superAdmin') {
        const steps = [];
        let finalText = null;
        let streamError = null;

        try {
          const geminiMessages = conversationHistory.slice(1).map(m => ({
            role: m.role === 'assistant' ? 'model' : 'user',
            parts: [{ text: m.content }],
          }));

          const fetchResponse = await fetchFunction('superAdminAgentStream', { messages: geminiMessages, userMessage });

          if (!fetchResponse.ok) {
            const err = await fetchResponse.json().catch(() => ({}));
            throw new Error(err.error || `HTTP ${fetchResponse.status}`);
          }

          await readSSEStream(fetchResponse, (event) => {
            if (event.type === 'thinking') {
              setProgressMessage('Analyzing your question...');
            } else if (event.type === 'tool_call') {
              steps.push({ tool: event.tool, args: event.args, summary: null });
              setProgressMessage(TOOL_LABELS[event.tool] || 'Querying Firestore...');
            } else if (event.type === 'tool_result') {
              const last = steps[steps.length - 1];
              if (last && last.tool === event.tool && last.summary === null) {
                last.summary = event.resultSummary;
              } else {
                steps.push({ tool: event.tool, args: null, summary: event.resultSummary });
              }
              setProgressMessage(event.resultSummary);
            } else if (event.type === 'text') {
              finalText = event.text;
            } else if (event.type === 'error') {
              streamError = event.message;
            }
          });
        } catch (err) {
          console.error('Error in superAdmin conversation:', err);
          streamError = err.message || 'Failed to reach the assistant.';
        }

        if (streamError) {
          setConversationHistory(prev => [
            ...prev,
            { role: 'user', content: userMessage },
            { role: 'assistant', content: streamError, steps, error: true },
          ]);
        } else if (finalText !== null) {
          setConversationHistory(prev => [
            ...prev,
            { role: 'user', content: userMessage },
            { role: 'assistant', content: finalText, steps },
          ]);
        }
      } else {
        const fetchResponse = await fetchFunction('conversationEventGeneratorCallable', { conversationHistory, userMessage, eventData });

        if (!fetchResponse.ok) throw new Error(`HTTP ${fetchResponse.status}`);

        let finalResult = null;
        await readSSEStream(fetchResponse, (event) => {
          if (event.type === 'step') {
            setProgressMessage(event.message);
          } else if (event.type === 'done') {
            finalResult = event.data;
          } else if (event.type === 'error') {
            throw new Error(event.message);
          }
        });

        if (finalResult) {
          if (finalResult.type === 'event') {
            onExtractedData((prev) => ({
              ...(prev || {}),
              ...finalResult.data,
              id: prev?.id || finalResult.data.id,
              date: finalResult.data.date
                ? (typeof finalResult.data.date === 'string' ? new Date(finalResult.data.date) : finalResult.data.date)
                : prev?.date,
            }));
            setExtractedData(finalResult.data);
            setConversationHistory(finalResult.conversationHistory);
            setIsChatCollapsed(true);
            showToast('Event generated successfully!', { type: 'success' });
          } else {
            setConversationHistory(finalResult.conversationHistory);
          }
        }
      }
    } catch (error) {
      console.error('Error in conversation:', error);
      showToast('Failed to process message. Please try again.', { type: 'error' });
    } finally {
      setIsProcessing(false);
      setPendingMessage(null);
      setProgressMessage('');
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startNewConversation = () => {
    if (onNewChat) {
      onNewChat();
    } else {
      setConversationHistory([{ role: 'assistant', content: getInitialMessage(type, eventData) }]);
    }
    setExtractedData(null);
    setCurrentMessage('');
    setPendingMessage(null);
  };

  return (
    <div className={`max-w-4xl mx-auto bg-primary bg-opacity-10 border border-primary rounded-lg p-4${fullHeight ? ' h-full flex flex-col' : ''}`}>
      {extractedData && (
        <div className="overflow-y-auto my-4 p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="text-sm font-medium border border-primary text-primaryDark mb-2 p-4 bg-primary bg-opacity-10 rounded-md text-center text-shadow-[1px_1px_2px_rgba(0,0,0,0.5)]">
            Please review and create from the Form (at the bottom)
          </h4>
          <div className="space-y-1 text-sm">
            {Object.entries(extractedData).map(([key, value]) => (
              <div key={key}>
                <div className="font-light text-gray-500 dark:text-gray-400 text-sm capitalize mb-1">{key.replace(/([A-Z])/g, ' $1')}:</div>
                <div className="text-gray-800 dark:text-gray-200 max-h-[300px] overflow-y-auto" style={typeof value === 'string' ? { whiteSpace: 'pre-wrap' } : undefined}>
                  {String(value)}
                </div>
              </div>
            ))}
          </div>
          {type === 'email' && onAccept && (
            <div className="mt-4 flex justify-center">
              <PrimaryButtonSmall onClick={() => onAccept(extractedData)}>
                Accept & Compose
              </PrimaryButtonSmall>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between my-1 cursor-pointer" onClick={() => setIsChatCollapsed(!isChatCollapsed)}>
        <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-200">
          <svg className="w-4 h-4 mr-1 text-primaryDark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          {type === 'email' ? 'Reservation Assistant' : type === 'superAdmin' ? 'Admin AI Assistant' : 'Brainstorm Event Ideas'}
        </label>
        <button
          className="p-1 text-primary hover:text-primary-dark focus:outline-none"
          aria-label={isChatCollapsed ? 'Expand Chat' : 'Collapse Chat'}
        >
          <svg
            className={`w-4 h-4 transition-transform ${isChatCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {!isChatCollapsed && (
        <div className={fullHeight ? 'flex flex-col flex-1 min-h-0' : ''}>
          <div className="flex mb-3 text-xs justify-between pl-4">
            <p className="text-gray-600 dark:text-gray-300 truncate mr-2">
              {type === 'superAdmin' ? 'Query your platform data with natural language' : `Chat to create your ${type} step by step`}
            </p>
            <button
              onClick={startNewConversation}
              className="flex items-center text-primary hover:text-primary-dark underline mr-2 whitespace-nowrap"
              disabled={isProcessing}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              New Chat
            </button>
          </div>

          <div
            ref={chatContainerRef}
            className={`${fullHeight ? 'flex-1 min-h-0' : 'h-64'} overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md p-3 mb-3 bg-white dark:bg-gray-800`}
          >
            <div className="space-y-3">
              {conversationHistory.map((message, index) => (
                <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-3 py-2 rounded-lg text-sm ${
                    message.role === 'user'
                      ? 'bg-primary text-white'
                      : message.error
                        ? 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}>
                    {message.role === 'assistant'
                      ? (message.error ? message.content : parseMarkdown(message.content))
                      : message.content}
                    {message.role === 'assistant' && message.steps?.length > 0 && (
                      <details className="mt-2 text-xs">
                        <summary className={`cursor-pointer ${message.error ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'} hover:underline`}>
                          Steps taken ({message.steps.length})
                        </summary>
                        <ol className="mt-1 ml-4 space-y-1.5 list-decimal text-gray-600 dark:text-gray-300">
                          {message.steps.map((step, i) => (
                            <li key={i}>
                              <span className="font-mono text-gray-800 dark:text-gray-200">{step.tool}</span>
                              {step.args && (
                                <code className="block mt-0.5 text-[10px] text-gray-500 dark:text-gray-400 break-all whitespace-pre-wrap">
                                  {JSON.stringify(step.args)}
                                </code>
                              )}
                              {step.summary && (
                                <span className="block mt-0.5 text-gray-700 dark:text-gray-200">→ {step.summary}</span>
                              )}
                            </li>
                          ))}
                        </ol>
                      </details>
                    )}
                  </div>
                </div>
              ))}
              {pendingMessage && (
                <div className="flex justify-end">
                  <div className="max-w-xs lg:max-w-md px-3 py-2 rounded-lg text-sm bg-primary text-white">
                    {pendingMessage.content}
                  </div>
                </div>
              )}
              {isProcessing && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 px-3 py-2 rounded-lg text-sm">
                    <div className="flex items-center space-x-2">
                      <div className="animate-pulse">{progressMessage || 'AI is thinking...'}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex space-x-2 items-end">
            <textarea
              ref={inputRef}
              rows={1}
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm resize-none max-h-48 overflow-y-auto"
            />
            <PrimaryButtonSmall
              type="button"
              onClick={handleSendMessage}
              loading={isProcessing}
              disabled={!currentMessage.trim() || isProcessing}
            >
              Send
            </PrimaryButtonSmall>
          </div>
        </div>
      )}
    </div>
  );
};

export default AITextExtractor;
